import type { DailyTacticalReadiness } from '../../services/operationsApi';
import type { FableMarketView } from '../../hooks/useFableLab';
import type { FableOpsAutomationView } from '../../hooks/useFableLab';
import { FableTone, modeLabel } from './fableLabels';

/** 10초 안에 답해야 하는 질문에 대한 한 줄 종합 판정. */
export default function FableVerdict({ readiness, market, opsAutomation }: Props) {
  const verdict = buildVerdict(readiness, market, opsAutomation);
  return (
    <section className={`fable-verdict tone-${verdict.tone}`}>
      <span className="fable-verdict-icon" aria-hidden>
        {verdict.tone === 'safe' ? '✓' : verdict.tone === 'danger' ? '!' : 'i'}
      </span>
      <div>
        <h2>{verdict.headline}</h2>
        <p>{verdict.detail}</p>
      </div>
    </section>
  );
}

function buildVerdict(
  readiness: DailyTacticalReadiness | null,
  market: FableMarketView | null,
  opsAutomation: FableOpsAutomationView | null,
) {
  if (!readiness) {
    return {
      tone: 'warn' as FableTone,
      headline: '시스템 상태를 불러오지 못했습니다',
      detail: '아래 각 칸의 API 오류 내용을 확인하세요. 상태를 알 수 없을 때는 아무것도 실행하지 않는 것이 원칙입니다.',
    };
  }
  const safety = readiness.live_order_safety;
  const broken =
    safety.live_broker_orders_count > 0 ||
    safety.broker_submit_allowed ||
    safety.real_capital_allowed;
  if (broken) {
    return {
      tone: 'danger' as FableTone,
      headline: '주의: 실전 잠금 상태가 예상과 다릅니다',
      detail: `실제 주문 ${safety.live_broker_orders_count}건 · 주문 전송 ${safety.broker_submit_allowed ? '허용됨' : '잠김'} · 실제 돈 ${safety.real_capital_allowed ? '허용됨' : '잠김'} — 즉시 사용자에게 보고가 필요합니다.`,
    };
  }
  const opsIssues = opsAutomation?.blockerAlert.blockers.length ?? 0;
  const opsNeedsAction = Boolean(
    opsAutomation?.blockerAlert.alert_required ||
    (opsAutomation && !opsAutomation.opsReport.ready_for_stage_a_daily_ops),
  );
  if (opsNeedsAction) {
    return {
      tone: 'warn' as FableTone,
      headline: `실제 돈과 주문은 안전하지만, 검증 문제 ${opsIssues || 1}건을 확인해야 합니다`,
      detail: `실제 돈 0원, 실제 주문 0건, 실전 잠금은 유지 중입니다. 문제를 해결하거나 영향이 없음을 확인하기 전에는 다음 단계로 넘어가지 않습니다.`,
    };
  }
  const stage = modeLabel(readiness.flags.cohort_mode);
  const waiting = market
    ? market.isOpen
      ? '미국장이 열려 있어, 조건이 맞으면 리허설용 수동 실행을 검토할 수 있습니다.'
      : '미국장이 닫혀 있어 다음 개장을 기다리는 중입니다.'
    : '시장 상태는 아래 칸에서 확인하세요.';
  return {
    tone: 'safe' as FableTone,
    headline: `지금은 '${stage}' 단계 — 실제 돈 0원, 실제 주문 0건, 실전 잠금 유지 중`,
    detail: `시스템은 멈춘 것이 아니라 4주간 공식 관찰을 시작할 조건을 준비하며 기다리는 중입니다. ${waiting}`,
  };
}

type Props = {
  readiness: DailyTacticalReadiness | null;
  market: FableMarketView | null;
  opsAutomation: FableOpsAutomationView | null;
};
